/**
 * Renders the SELECTED mission's waypoint route on the map — numbered
 * markers, dashed connecting line with direction chevrons, per-waypoint
 * heading arrows, and amber outbound/return legs anchored to the bound
 * dock's home point.
 *
 * Lifecycle: mounted at EditorPage level alongside DockHomeLayer so the
 * route stays visible regardless of which right-rail ticket is open. The
 * placement-click handler (only relevant while the dock-mission panel is
 * open) lives separately in useDemoWaypointPlacement.
 *
 * Self-healing: on every `styledata` event we re-install missing sources/
 * layers, not just re-pin. This protects against any external code that
 * removes our layers (style swap that races our handler, MapCanvas hooks
 * that touch the style during teardown, etc.) — without this guard, a
 * single accidental removal would silently strand the route until the
 * next style.load.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { Map as MapboxMap, MapLayerMouseEvent } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type { DemoMission, DemoDockStation } from './types';

const SRC_WAYPOINTS = 'dock-demo-waypoints-src';
const SRC_LINE = 'dock-demo-waypoints-line-src';
const SRC_HOME_LEGS = 'dock-demo-home-legs-src';
const LAYER_HOME_LEGS = 'dock-demo-home-legs-lyr';
const LAYER_HOME_LEGS_ARROWS = 'dock-demo-home-legs-arrows-lyr';
const LAYER_LINE_HIT = 'dock-demo-waypoints-line-hit-lyr';
const LAYER_LINE = 'dock-demo-waypoints-line-lyr';
const LAYER_LINE_ARROWS = 'dock-demo-waypoints-line-arrows-lyr';
const LAYER_CIRCLE = 'dock-demo-waypoints-circle-lyr';
const LAYER_LABEL = 'dock-demo-waypoints-label-lyr';
const LAYER_HEADING = 'dock-demo-waypoints-heading-lyr';

const ALL_LAYERS = [
  // Home legs render under the route line so they don't visually cut across
  // the survey waypoints; pinLayersToTop preserves this relative ordering.
  LAYER_HOME_LEGS,
  LAYER_HOME_LEGS_ARROWS,
  // Invisible wide hit-target line sits underneath the visible dashed
  // route line so clicks anywhere within ~9 px of the line still register
  // (the visible line is only 2.5 px wide, too narrow for reliable hit
  // detection — same trick used by the NFZ outline-hit layer).
  LAYER_LINE_HIT,
  LAYER_LINE,
  LAYER_LINE_ARROWS,
  LAYER_CIRCLE,
  LAYER_LABEL,
  LAYER_HEADING,
] as const;
const ALL_SOURCES = [SRC_WAYPOINTS, SRC_LINE, SRC_HOME_LEGS] as const;

/**
 * Initial bearing (degrees, 0..360, clockwise from north) along the great-
 * circle from `a` to `b`. Used both for per-waypoint heading fallback and
 * for chevron rotation along the connecting line.
 */
function bearingDeg(a: [number, number], b: [number, number]): number {
  const lng1 = (a[0] * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lng2 = (b[0] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function buildPointFeatures(mission: DemoMission | undefined) {
  // Source data is always populated for the selected mission. Visibility
  // is toggled at the LAYER level via setLayoutProperty('visibility',…),
  // not by clearing the source. This is much lighter than setData(empty)
  // and avoids interrupting Mapbox's symbol placement engine.
  if (!mission) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  const wps = mission.waypoints;
  return {
    type: 'FeatureCollection' as const,
    features: wps.map((w, idx) => {
      let heading: number;
      if (w.heading !== undefined) {
        heading = w.heading;
      } else if (idx < wps.length - 1) {
        heading = bearingDeg([w.lng, w.lat], [wps[idx + 1].lng, wps[idx + 1].lat]);
      } else if (idx > 0) {
        heading = bearingDeg([wps[idx - 1].lng, wps[idx - 1].lat], [w.lng, w.lat]);
      } else {
        heading = 0;
      }
      return {
        type: 'Feature' as const,
        // Top-level `id` is what map.setFeatureState targets. Without it,
        // hover / select highlighting via `['feature-state', ...]` paint
        // expressions can't address individual waypoints. The id is also
        // duplicated into properties because Mapbox feature events expose
        // properties (not the top-level id) on the e.features payload.
        id: w.id,
        properties: {
          index: idx + 1,
          id: w.id,
          alt: w.alt,
          action: w.action,
          heading,
        },
        geometry: { type: 'Point' as const, coordinates: [w.lng, w.lat] },
      };
    }),
  };
}

function buildLineFeatures(mission: DemoMission | undefined) {
  if (!mission || mission.waypoints.length < 2) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  // Walk waypoints carrying the per-leg recording state forward (leg
  // params with carry-over semantics). Each leg between consecutive
  // waypoints becomes its own LineString feature with a `recording`
  // boolean — the route layer's paint expression switches colour on it.
  //
  // The recording state is sourced from each waypoint's `legParams`
  // (which describes the leg leaving that waypoint), with undefined
  // meaning "inherit from previous leg" — matches DJI's actual gimbal/
  // recording semantics and the WPML exporter's behaviour.
  const wps = mission.waypoints;
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  let recording = false;
  for (let i = 0; i < wps.length - 1; i++) {
    const lp = wps[i].legParams;
    if (lp?.recordVideo !== undefined) recording = lp.recordVideo;
    features.push({
      type: 'Feature',
      // `segmentIndex` lets the click-to-insert handler resolve which leg
      // the user clicked on without re-scanning all waypoints in JS.
      properties: { recording, segmentIndex: i },
      geometry: {
        type: 'LineString',
        coordinates: [
          [wps[i].lng, wps[i].lat],
          [wps[i + 1].lng, wps[i + 1].lat],
        ],
      },
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

/**
 * Resolve the dock home anchor for the route's outbound / return legs.
 * Lazy lookup against `mission.dockStationId` — returns null when:
 *   - mission has no dockStationId (truly unbound)
 *   - dockStationId set but no dock matches (deleted)
 *   - dock exists but lng/lat still 0,0 (un-located)
 * In all three cases the legs simply don't render.
 */
function resolveHomePoint(
  stations: DemoDockStation[],
  mission: DemoMission | undefined,
): [number, number] | null {
  if (!mission || !mission.dockStationId) return null;
  const dock = stations.find((s) => s.id === mission.dockStationId);
  if (!dock) return null;
  if (dock.lng === 0 && dock.lat === 0) return null;
  return [dock.lng, dock.lat];
}

/**
 * Outbound leg (home → first waypoint) shows as soon as a waypoint exists.
 * Return leg (last waypoint → home) only shows once the user has stopped
 * placing — while placementMode is on, the "last waypoint" is in flux and
 * a flickering return line would be confusing.
 */
function buildHomeLegFeatures(
  mission: DemoMission | undefined,
  homePoint: [number, number] | null,
  includeReturn: boolean,
) {
  if (!mission || mission.waypoints.length === 0 || !homePoint) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  const wps = mission.waypoints;
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [
    {
      type: 'Feature',
      properties: { kind: 'outbound' },
      geometry: {
        type: 'LineString',
        coordinates: [homePoint, [wps[0].lng, wps[0].lat]],
      },
    },
  ];
  if (includeReturn) {
    features.push({
      type: 'Feature',
      properties: { kind: 'return' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [wps[wps.length - 1].lng, wps[wps.length - 1].lat],
          homePoint,
        ],
      },
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_WAYPOINTS)) {
    map.addSource(SRC_WAYPOINTS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_LINE)) {
    map.addSource(SRC_LINE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_HOME_LEGS)) {
    map.addSource(SRC_HOME_LEGS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(LAYER_HOME_LEGS)) {
    map.addLayer({
      id: LAYER_HOME_LEGS,
      type: 'line',
      source: SRC_HOME_LEGS,
      paint: {
        'line-color': '#fbbf24',
        'line-width': 2.5,
        'line-dasharray': [4, 2],
        'line-opacity': 0.85,
      },
    });
  }
  if (!map.getLayer(LAYER_HOME_LEGS_ARROWS)) {
    map.addLayer({
      id: LAYER_HOME_LEGS_ARROWS,
      type: 'symbol',
      source: SRC_HOME_LEGS,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 80,
        'text-field': '▶',
        'text-size': 12,
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#fbbf24',
        'text-halo-color': 'rgba(120, 53, 15, 0.9)',
        'text-halo-width': 1.2,
      },
    });
  }
  if (!map.getLayer(LAYER_LINE_HIT)) {
    // Invisible wide line on the route source — acts as a generous click
    // target for the "click on the route to insert a waypoint here" UX.
    // line-width 18 gives a ±9 px tolerance around the visible 2.5 px line.
    map.addLayer({
      id: LAYER_LINE_HIT,
      type: 'line',
      source: SRC_LINE,
      paint: {
        'line-color': '#000000',
        'line-width': 18,
        'line-opacity': 0,
      },
    });
  }
  if (!map.getLayer(LAYER_LINE)) {
    // Per-segment colour: recording legs render in red so the user can see
    // exactly which stretch of the mission is captured on video. Mapbox's
    // case expression evaluates the `recording` property attached to each
    // segment feature (built in buildLineFeatures).
    map.addLayer({
      id: LAYER_LINE,
      type: 'line',
      source: SRC_LINE,
      paint: {
        'line-color': [
          'case',
          ['==', ['get', 'recording'], true], '#ef4444',
          '#22d3ee',
        ],
        'line-width': [
          'case',
          ['==', ['get', 'recording'], true], 3.5,
          2.5,
        ],
        'line-dasharray': [2, 1.5],
      },
    });
  }
  if (!map.getLayer(LAYER_LINE_ARROWS)) {
    map.addLayer({
      id: LAYER_LINE_ARROWS,
      type: 'symbol',
      source: SRC_LINE,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 60,
        'text-field': '▶',
        'text-size': 12,
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': [
          'case',
          ['==', ['get', 'recording'], true], '#ef4444',
          '#22d3ee',
        ],
        'text-halo-color': [
          'case',
          ['==', ['get', 'recording'], true], 'rgba(127, 29, 29, 0.95)',
          'rgba(8, 145, 178, 0.9)',
        ],
        'text-halo-width': 1.2,
      },
    });
  }
  if (!map.getLayer(LAYER_CIRCLE)) {
    // Per-feature highlighting via Mapbox feature-state — set by the layer's
    // pointer-interaction useEffect when the hovered / selected waypoint id
    // changes. The circle grows on hover and gains an amber stroke when
    // selected so panel-side selection feels synced to the map.
    map.addLayer({
      id: LAYER_CIRCLE,
      type: 'circle',
      source: SRC_WAYPOINTS,
      paint: {
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'hovered'], false], 16,
          ['boolean', ['feature-state', 'selected'], false], 14,
          12,
        ],
        'circle-color': '#0891b2',
        'circle-stroke-width': [
          'case',
          ['boolean', ['feature-state', 'selected'], false], 3,
          2,
        ],
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false], '#fbbf24',
          '#ffffff',
        ],
      },
    });
  }
  if (!map.getLayer(LAYER_LABEL)) {
    map.addLayer({
      id: LAYER_LABEL,
      type: 'symbol',
      source: SRC_WAYPOINTS,
      layout: {
        'text-field': ['get', 'index'],
        'text-size': 13,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
  }
  if (!map.getLayer(LAYER_HEADING)) {
    // Heading arrow rendered next to each waypoint, pointing in the
    // per-waypoint effective heading. Coordinate gymnastics: Mapbox applies
    // text-offset in the symbol's UNROTATED frame and then rotates by
    // text-rotate. The ➤ glyph naturally points east, so:
    //   - rotation = heading − 90 makes the arrow point along the heading
    //   - offset = [1.5, 0] in the unrotated frame rotates with the symbol
    //     and sits on the heading-side of the marker with its tip pointing
    //     in the heading direction.
    map.addLayer({
      id: LAYER_HEADING,
      type: 'symbol',
      source: SRC_WAYPOINTS,
      layout: {
        'text-field': '➤',
        'text-size': 16,
        'text-rotate': ['-', ['coalesce', ['get', 'heading'], 0], 90],
        'text-rotation-alignment': 'map',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-offset': [1.5, 0],
      },
      paint: {
        'text-color': '#fbbf24',
        'text-halo-color': 'rgba(15, 23, 42, 0.85)',
        'text-halo-width': 1.4,
      },
    });
  }
}

function pinLayersToTop(map: MapboxMap): void {
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore — layer may not exist yet on first call before ensure
    }
  }
}

/**
 * Toggle the route's on-map visibility via Mapbox layer-level visibility.
 * Used instead of `setData([])` when only the visibility flag flipped —
 * setLayoutProperty doesn't trigger source rebuild or symbol re-placement,
 * so it's both lighter on perf and safer alongside the platform's own
 * symbol layers (no continuePlacement risk).
 */
function setRouteVisibility(map: MapboxMap, visible: boolean): void {
  const value = visible ? 'visible' : 'none';
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', value);
    } catch {
      // ignore — layer may not exist yet
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch {
      // map may be in teardown; defensive
    }
  }
  for (const id of ALL_SOURCES) {
    try {
      if (map.getSource(id)) map.removeSource(id);
    } catch {
      // ignore
    }
  }
}

/**
 * Returns true when at least one of our sources or layers is missing on the
 * given map. Used by the styledata recovery path to detect when external
 * code has wiped our state and a full reinstall is needed (vs. just a pin).
 */
function isMissing(map: MapboxMap): boolean {
  for (const id of ALL_SOURCES) if (!map.getSource(id)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

export default function DockMissionRouteLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const missions = useDockDemoStore((s) => s.missions);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const placementMode = useDockDemoStore((s) => s.placementMode);
  const dockStations = useDockDemoStore((s) => s.dockStations);
  const hoveredWaypointId = useDockDemoStore((s) => s.hoveredWaypointId);
  const selectedWaypointId = useDockDemoStore((s) => s.selectedWaypointId);

  // Drag state for the waypoint reposition gesture. Held in a ref so the
  // window-level mousemove handler can read it without re-binding on every
  // frame; pointerMoved gates the click→select handler so a real drag
  // doesn't also fire a selection at the final position.
  const dragRef = useRef<null | { missionId: string; waypointId: string; pointerMoved: boolean }>(null);
  // Track the previously-highlighted ids so the feature-state sync effect
  // only has to clear the OLD id rather than walk the entire source.
  const prevHoverIdRef = useRef<string | null>(null);
  const prevSelectIdRef = useRef<string | null>(null);

  const mission = missions.find((m) => m.id === selectedMissionId);

  // Memoize on the bound dock's coords + waypoint count + dockStationId so a
  // mission edit that doesn't change the home (e.g. a name edit) doesn't
  // thrash setData / pin loops.
  const homePoint = useMemo(
    () => resolveHomePoint(dockStations, mission),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dockStations, mission?.dockStationId],
  );
  const includeReturn = !placementMode;

  // Effect 1 — install / teardown lifecycle + direct store subscription.
  // Deps deliberately exclude store slices so a waypoint click doesn't
  // tear down + re-create every layer. Belt-and-braces: we subscribe to
  // the store directly here AND keep the deps-based Effect 2 below. Either
  // path will push fresh data on a visibility toggle; whichever fires
  // first wins, and the other is a cheap no-op duplicate.
  useEffect(() => {
    if (!map || !isMapReady) return;

    // setData on an existing source is safe even when isStyleLoaded() is
    // transiently false — only addSource / addLayer require a loaded style,
    // and pinLayersToTop's moveLayer also needs it. We split the work:
    // ensureSourcesAndLayers gates on isStyleLoaded; setData always runs
    // (try/catch) when its source exists. This matters because in dev mode
    // MapboxDraw's connect can wedge isStyleLoaded permanently false, and a
    // hard early-return here would mean missions arriving from a delayed
    // setActiveProject(siteId) never render until the user manually re-
    // triggers a state change. See reference_mapbox_setdata_gating memory.
    const reinstall = () => {
      const styleLoaded = map.isStyleLoaded();
      if (styleLoaded) {
        ensureSourcesAndLayers(map);
      }
      const s = useDockDemoStore.getState();
      const m = s.missions.find((x) => x.id === s.selectedMissionId);
      const home = resolveHomePoint(s.dockStations, m);
      const ptSrc = map.getSource(SRC_WAYPOINTS);
      const lnSrc = map.getSource(SRC_LINE);
      const legSrc = map.getSource(SRC_HOME_LEGS);
      if (ptSrc && 'setData' in ptSrc) {
        try { ptSrc.setData(buildPointFeatures(m)); } catch { /* style mid-mutation */ }
      }
      if (lnSrc && 'setData' in lnSrc) {
        try { lnSrc.setData(buildLineFeatures(m)); } catch { /* same */ }
      }
      if (legSrc && 'setData' in legSrc) {
        try { legSrc.setData(buildHomeLegFeatures(m, home, !s.placementMode)); } catch { /* same */ }
      }
      // Layer visibility tracks the selected mission's `visible` flag.
      // Source data above stays populated regardless — only the rendered
      // visibility flips. Lighter than setData([]) and doesn't disturb
      // Mapbox's symbol placement engine on toggle.
      setRouteVisibility(map, !!m && m.visible !== false);
      if (styleLoaded) pinLayersToTop(map);
    };

    const repin = () => {
      if (isMissing(map)) {
        reinstall();
      } else {
        pinLayersToTop(map);
      }
    };

    if (map.isStyleLoaded()) reinstall();
    map.on('style.load', reinstall);
    map.on('styledata', repin);

    // Direct store subscription — slice-aware. We track snapshot refs
    // ourselves (rather than using the listener's prev arg) for resilience
    // against Zustand v5 listener-signature quirks.
    //
    // reinstall is deferred via requestAnimationFrame so it lands at frame
    // boundaries — see DemoMissionMapLayer for the full rationale (Mapbox's
    // symbol placement engine is shared across all layers and an interrupt-
    // ing setData mid-placement crashes continuePlacement, which could
    // affect platform symbol layers too).
    let prevSnap = (() => {
      const s = useDockDemoStore.getState();
      return {
        missions: s.missions,
        selectedMissionId: s.selectedMissionId,
        placementMode: s.placementMode,
        dockStations: s.dockStations,
      };
    })();
    let rafId: number | null = null;
    const queueReinstall = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        reinstall();
      });
    };
    const unsubscribe = useDockDemoStore.subscribe(() => {
      const s = useDockDemoStore.getState();
      const changed =
        s.missions !== prevSnap.missions ||
        s.selectedMissionId !== prevSnap.selectedMissionId ||
        s.placementMode !== prevSnap.placementMode ||
        s.dockStations !== prevSnap.dockStations;
      if (changed) {
        prevSnap = {
          missions: s.missions,
          selectedMissionId: s.selectedMissionId,
          placementMode: s.placementMode,
          dockStations: s.dockStations,
        };
        queueReinstall();
      }
    });

    return () => {
      unsubscribe();
      if (rafId !== null) cancelAnimationFrame(rafId);
      map.off('style.load', reinstall);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady]);

  // Effect 2 — data update on every mission / dock-home / placement-mode
  // change. The selectors above subscribe to the relevant slices; when any
  // of them changes, this effect re-runs and pushes fresh data to Mapbox.
  // Direct values from selectors (not derived arrays) so effect-dep
  // comparison is reliable.
  //
  // Same isStyleLoaded-decoupling as reinstall above — setData runs
  // unconditionally with try/catch so a wedged style doesn't strand the
  // data; ensureSourcesAndLayers + moveLayer still gate.
  useEffect(() => {
    if (!map || !isMapReady) return;
    const styleLoaded = map.isStyleLoaded();
    if (styleLoaded) {
      ensureSourcesAndLayers(map);
    }
    const ptSrc = map.getSource(SRC_WAYPOINTS);
    const lnSrc = map.getSource(SRC_LINE);
    const legSrc = map.getSource(SRC_HOME_LEGS);
    if (ptSrc && 'setData' in ptSrc) {
      try { ptSrc.setData(buildPointFeatures(mission)); } catch { /* style mid-mutation */ }
    }
    if (lnSrc && 'setData' in lnSrc) {
      try { lnSrc.setData(buildLineFeatures(mission)); } catch { /* same */ }
    }
    if (legSrc && 'setData' in legSrc) {
      try { legSrc.setData(buildHomeLegFeatures(mission, homePoint, includeReturn)); } catch { /* same */ }
    }
    setRouteVisibility(map, !!mission && mission.visible !== false);
    if (styleLoaded) pinLayersToTop(map);
  }, [map, isMapReady, mission, homePoint, includeReturn]);

  // Effect 3 — pointer interactions on waypoints + route line.
  //   - Hover on waypoint circle → cursor 'grab' + setHoveredWaypointId
  //   - Mousedown on circle → start drag; window mousemove updates lng/lat
  //   - Click on circle → setSelectedWaypointId (suppressed if real drag)
  //   - Right-click on circle → setContextMenuTarget for the popover
  //   - Hover on the route's invisible hit line → cursor 'copy'
  //   - Click on hit line → insertWaypointAt(segmentIndex+1, lng, lat)
  useEffect(() => {
    if (!map || !isMapReady) return;

    const onCircleMouseEnter = (e: MapLayerMouseEvent) => {
      if (dragRef.current) return;
      const feat = e.features?.[0];
      const id = feat?.properties?.id as string | undefined;
      if (id) useDockDemoStore.getState().setHoveredWaypointId(id);
      map.getCanvas().style.cursor = 'grab';
    };
    const onCircleMouseLeave = () => {
      if (dragRef.current) return;
      useDockDemoStore.getState().setHoveredWaypointId(null);
      map.getCanvas().style.cursor = '';
    };

    const onCircleMouseDown = (e: MapLayerMouseEvent) => {
      const feat = e.features?.[0];
      const id = feat?.properties?.id as string | undefined;
      if (!id) return;
      const cur = useDockDemoStore.getState();
      const m = cur.missions.find((x) => x.id === cur.selectedMissionId);
      if (!m) return;
      // While placement mode is on, the next click is meant to APPEND a new
      // waypoint at that location, not start a drag — bail so the placement
      // handler in useDemoWaypointPlacement still wins.
      if (cur.placementMode) return;
      dragRef.current = { missionId: m.id, waypointId: id, pointerMoved: false };
      map.getCanvas().style.cursor = 'grabbing';
      // Prevent Mapbox from initiating its own map drag on this gesture.
      e.preventDefault();
    };

    const onWindowMouseMove = (ev: MouseEvent) => {
      const ds = dragRef.current;
      if (!ds) return;
      ds.pointerMoved = true;
      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const ll = map.unproject([ev.clientX - rect.left, ev.clientY - rect.top]);
      useDockDemoStore.getState().updateWaypoint(ds.missionId, ds.waypointId, {
        lng: ll.lng,
        lat: ll.lat,
      });
    };

    const onWindowMouseUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      map.getCanvas().style.cursor = '';
    };

    const onCircleClick = (e: MapLayerMouseEvent) => {
      // Click after a real drag is suppressed — the drag was the intent.
      // Mapbox's own click event still fires here; the pointerMoved check
      // on the just-cleared dragRef (set inline below) is unreliable since
      // mouseup already cleared it, so we instead inspect the originalEvent
      // which Mapbox synthesizes — but simpler: we leave the gate to the
      // pointerMoved flag captured INSIDE the dragRef during mousemove and
      // checked when mouseup runs. Since mouseup runs before click, we
      // stash the last drag's "actually moved" verdict in a ref-local.
      // Simplest pragmatic gate: ignore click when the originalEvent says
      // it was preceded by motion. Mapbox's MapMouseEvent doesn't expose
      // that directly, so we rely on a separate ref instead.
      void e;
      const justDragged = lastDragMovedRef.current;
      lastDragMovedRef.current = false;
      if (justDragged) return;
      const feat = e.features?.[0];
      const id = feat?.properties?.id as string | undefined;
      if (!id) return;
      useDockDemoStore.getState().setSelectedWaypointId(id);
    };

    const onCircleContextMenu = (e: MapLayerMouseEvent) => {
      e.preventDefault();
      const feat = e.features?.[0];
      const id = feat?.properties?.id as string | undefined;
      if (!id) return;
      const cur = useDockDemoStore.getState();
      const m = cur.missions.find((x) => x.id === cur.selectedMissionId);
      if (!m) return;
      const originalEvent = e.originalEvent as MouseEvent;
      useDockDemoStore.getState().setContextMenuTarget({
        missionId: m.id,
        waypointId: id,
        x: originalEvent.clientX,
        y: originalEvent.clientY,
      });
    };

    const onLineMouseEnter = () => {
      if (dragRef.current) return;
      // Don't override the hover-line cursor while in placement mode — the
      // crosshair already signals "next click adds a waypoint".
      const cur = useDockDemoStore.getState();
      if (cur.placementMode) return;
      map.getCanvas().style.cursor = 'copy';
    };
    const onLineMouseLeave = () => {
      if (dragRef.current) return;
      map.getCanvas().style.cursor = '';
    };

    const onLineClick = (e: MapLayerMouseEvent) => {
      const cur = useDockDemoStore.getState();
      // In placement mode, the global useDemoWaypointPlacement click handler
      // owns this click — appending a waypoint to the end of the mission.
      // We let it run unimpeded so the click-on-line affordance doesn't
      // override the dominant placement gesture.
      if (cur.placementMode) return;
      const feat = e.features?.[0];
      const segIdxRaw = feat?.properties?.segmentIndex;
      const segIdx = typeof segIdxRaw === 'number' ? segIdxRaw : Number(segIdxRaw);
      if (!Number.isFinite(segIdx)) return;
      const m = cur.missions.find((x) => x.id === cur.selectedMissionId);
      if (!m) return;
      // Insert AFTER waypoint[segIdx] so the click splits the clicked leg
      // (segIdx → segIdx+1). The raw click lng/lat is close enough to the
      // visible line at typical zoom — the user can drag the new waypoint
      // to refine if needed.
      cur.insertWaypointAt(m.id, segIdx + 1, e.lngLat.lng, e.lngLat.lat);
    };

    // Click-after-drag gate: window mouseup sets `lastDragMovedRef` from the
    // dragRef BEFORE the click fires, so the click handler can ignore the
    // synthesized click that follows a drag. Mapbox doesn't expose this
    // information natively on MapLayerMouseEvent.
    const lastDragMovedRef = { current: false };
    const onWindowMouseUpFlagged = () => {
      if (dragRef.current) lastDragMovedRef.current = dragRef.current.pointerMoved;
      onWindowMouseUp();
    };

    map.on('mouseenter', LAYER_CIRCLE, onCircleMouseEnter);
    map.on('mouseleave', LAYER_CIRCLE, onCircleMouseLeave);
    map.on('mousedown', LAYER_CIRCLE, onCircleMouseDown);
    map.on('click', LAYER_CIRCLE, onCircleClick);
    map.on('contextmenu', LAYER_CIRCLE, onCircleContextMenu);
    map.on('mouseenter', LAYER_LINE_HIT, onLineMouseEnter);
    map.on('mouseleave', LAYER_LINE_HIT, onLineMouseLeave);
    map.on('click', LAYER_LINE_HIT, onLineClick);
    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUpFlagged);

    return () => {
      map.off('mouseenter', LAYER_CIRCLE, onCircleMouseEnter);
      map.off('mouseleave', LAYER_CIRCLE, onCircleMouseLeave);
      map.off('mousedown', LAYER_CIRCLE, onCircleMouseDown);
      map.off('click', LAYER_CIRCLE, onCircleClick);
      map.off('contextmenu', LAYER_CIRCLE, onCircleContextMenu);
      map.off('mouseenter', LAYER_LINE_HIT, onLineMouseEnter);
      map.off('mouseleave', LAYER_LINE_HIT, onLineMouseLeave);
      map.off('click', LAYER_LINE_HIT, onLineClick);
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUpFlagged);
      // Safety: tear-down mid-drag re-cleans cursor state.
      if (dragRef.current) {
        map.getCanvas().style.cursor = '';
        dragRef.current = null;
      }
    };
  }, [map, isMapReady]);

  // Effect 4 — sync Mapbox feature-state with the store's hovered / selected
  // waypoint ids. Feature-state is the Mapbox-canonical way to highlight a
  // single feature without rebuilding the source. Paint expressions on
  // LAYER_CIRCLE read `['feature-state', 'hovered']` / 'selected' and bump
  // the radius / stroke color accordingly.
  useEffect(() => {
    if (!map || !isMapReady) return;
    // Tracking refs let us clear the previously-highlighted feature without
    // iterating all features each time (cheaper for missions with many
    // waypoints; the source-wide removeFeatureState API exists but takes
    // longer for large sources).
    if (prevHoverIdRef.current && prevHoverIdRef.current !== hoveredWaypointId) {
      try {
        map.removeFeatureState(
          { source: SRC_WAYPOINTS, id: prevHoverIdRef.current },
          'hovered',
        );
      } catch { /* feature may have been removed */ }
    }
    if (hoveredWaypointId) {
      try {
        map.setFeatureState(
          { source: SRC_WAYPOINTS, id: hoveredWaypointId },
          { hovered: true },
        );
      } catch { /* source may not exist yet */ }
    }
    prevHoverIdRef.current = hoveredWaypointId;

    if (prevSelectIdRef.current && prevSelectIdRef.current !== selectedWaypointId) {
      try {
        map.removeFeatureState(
          { source: SRC_WAYPOINTS, id: prevSelectIdRef.current },
          'selected',
        );
      } catch { /* same */ }
    }
    if (selectedWaypointId) {
      try {
        map.setFeatureState(
          { source: SRC_WAYPOINTS, id: selectedWaypointId },
          { selected: true },
        );
      } catch { /* same */ }
    }
    prevSelectIdRef.current = selectedWaypointId;
  }, [map, isMapReady, hoveredWaypointId, selectedWaypointId, mission]);

  return null;
}
